import { useEffect, useMemo, useState } from "react";
import { api, formatErr, API_BASE } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Film, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_TEMPLATE = {
  id: "fallback",
  name: "Default Layout",
  layout: {
    zones: [
      { id: "main", name: "Main" },
      { id: "sidebar", name: "Sidebar" },
      { id: "ticker", name: "Ticker" },
    ],
  },
};

const emptyForm = { name: "", template_id: "", zone_items: {}, ticker_messages: [] };

const legacyZoneOrder = ["main", "sidebar", "ticker"];

// Extract and normalize zones from a template's layout
const extractZonesFromTemplate = (template) => {
  if (!template) return [];
  const layoutZones = template.layout?.zones || [];
  if (Array.isArray(layoutZones) && layoutZones.length > 0) {
    return layoutZones.map((z) => ({
      id: z.id || z.name?.toLowerCase().replace(/\s+/g, '_'),
      name: z.name || z.id,
      role: z.role || z.type || "",
    }));
  }
  const layout = template.layout || {};
  const legacyZones = [];
  if (layout.main) legacyZones.push({ id: "main", name: layout.main || "Main", role: "" });
  if (layout.sidebar) legacyZones.push({ id: "sidebar", name: layout.sidebar || "Sidebar", role: "" });
  if (layout.ticker) legacyZones.push({ id: "ticker", name: layout.ticker || "Ticker", role: "ticker" });
  return legacyZones.length > 0 ? legacyZones : [];
};

const normalizeZoneItems = (zoneItems = {}) => {
  if (!zoneItems || typeof zoneItems !== "object") return {};
  return Object.fromEntries(Object.entries(zoneItems).filter(([, items]) => Array.isArray(items)));
};

const remapLegacyItemsToZones = (zoneDefs, existingZoneItems = {}) => {
  const cleaned = normalizeZoneItems(existingZoneItems);
  const nextZoneItems = {};

  if (zoneDefs.length === 0) return nextZoneItems;

  const hasMatchingKeys = zoneDefs.some((zone) => cleaned[zone.id]);
  if (hasMatchingKeys) {
    zoneDefs.forEach((zone) => {
      nextZoneItems[zone.id] = cleaned[zone.id] || [];
    });
    return nextZoneItems;
  }

  const legacyBuckets = legacyZoneOrder.map((key) => cleaned[key] || []);
  zoneDefs.forEach((zone, index) => {
    nextZoneItems[zone.id] = legacyBuckets[index] || [];
  });

  return nextZoneItems;
};

const isTickerZone = (zone) => zone?.role === "ticker" || /ticker/i.test(`${zone?.id || ""} ${zone?.name || ""}`);
const isQueueZone = (zone) => zone?.role === "queue" || /queue|token/i.test(`${zone?.id || ""} ${zone?.name || ""}`);
const isLogoZone = (zone) => zone?.role === "logo" || /logo|brand/i.test(`${zone?.id || ""} ${zone?.name || ""}`);

const isAutoZone = (zone) => /^(header|weather|bookings)$/i.test(zone?.role || "") || isQueueZone(zone);

const mediaUrl = (item) => `${API_BASE}/media/serve/${item.id}`;
const isImageMedia = (item) => item?.kind === "image" || (item?.content_type || "").startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(item?.original_filename || item?.name || "");
const isVideoMedia = (item) => item?.kind === "video" || (item?.content_type || "").startsWith("video/") || /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(item?.original_filename || item?.name || "");

function MediaThumb({ item, className = "h-full w-full object-cover", iconClassName = "w-4 h-4" }) {
  if (!item) {
    return <Film className={`${iconClassName} text-[#9CA3AF]`} />;
  }
  if (isImageMedia(item)) {
    return <img src={mediaUrl(item)} className={className} alt={item.name || ""} />;
  }
  if (isVideoMedia(item)) {
    return (
      <video
        src={`${mediaUrl(item)}#t=0.1`}
        className={className}
        muted
        playsInline
        preload="metadata"
        onLoadedMetadata={(e) => {
          const video = e.currentTarget;
          if (video.duration && video.currentTime < 0.1) video.currentTime = Math.min(0.1, video.duration / 2);
        }}
      />
    );
  }
  return <Film className={`${iconClassName} text-[#9CA3AF]`} />;
}

function ZonePicker({ media, items, setItems }) {
  const [addType, setAddType] = useState(""); // for managing add dialog
  const folders = useMemo(() => Array.from(new Set(media.map((item) => item.folder || "default"))), [media]);
  const [selectedFolder, setSelectedFolder] = useState("");
  const [textValue, setTextValue] = useState("");
  const [textFontSize, setTextFontSize] = useState(24);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [youtubeTitle, setYoutubeTitle] = useState("");
  const [newDuration, setNewDuration] = useState(10);

  useEffect(() => {
    if (!folders.length) {
      if (selectedFolder) setSelectedFolder("");
      return;
    }
    if (!selectedFolder || !folders.includes(selectedFolder)) setSelectedFolder(folders[0]);
  }, [folders, selectedFolder]);

  const folderMedia = selectedFolder
    ? media.filter((item) => (item.folder || "default") === selectedFolder)
    : [];

  const addMedia = (id) => {
    if (!items.some((i) => i.type === "media" && i.media_id === id)) {
      setItems([...items, { type: "media", media_id: id, fit: "cover", duration: 10 }]);
    }
  };

  const addText = () => {
    if (textValue.trim()) {
      setItems([...items, { type: "text", content: textValue, duration: newDuration, font_size: Math.max(12, Math.min(120, Number(textFontSize) || 24)) }]);
      setTextValue("");
      setTextFontSize(24);
      setNewDuration(10);
      setAddType("");
    }
  };

  const addYoutube = () => {
    if (youtubeUrl.trim()) {
      setItems([...items, { type: "youtube", url: youtubeUrl, title: youtubeTitle.trim() || "YouTube video" }]);
      setYoutubeUrl("");
      setYoutubeTitle("");
      setNewDuration(10);
      setAddType("");
    }
  };

  const addWidget = (type) => {
    const widgetDefaults = {
      clock: { type: "clock", title: "Today", duration: 10 },
      weather: { type: "weather", title: "Weather", location: "Local area", duration: 10 },
      bookings: { type: "bookings", title: "Today's bookings", duration: 14 },
      queue: { type: "queue", title: "Live queue", duration: 14 },
      notices: { type: "notices", title: "Notices", duration: 12 },
      society_directory: { type: "society_directory", title: "Owner Directory", wing: "All", display_mode: "wing_rotation", rows_per_page: 8, show_mobile: false, duration: 20 },
      restaurant_menu: { type: "restaurant_menu", title: "Our Menu", tagline: "", columns: 2, font_size: 24, show_images: false, show_descriptions: false, show_item_numbers: false, auto_rotate: true, page_seconds: 10, manual_navigation: true, background_color: "#f5f1e8", text_color: "#17212b", accent_color: "#233846", price_color: "#17212b", duration: 30 },
      products: { type: "products", title: "Products", limit: 8, duration: 14 },
      courses: { type: "courses", title: "Courses", limit: 8, duration: 14 },
      services: { type: "services", title: "Services", limit: 8, duration: 14 },
    };
    const next = widgetDefaults[type];
    if (!next) return;
    setItems([...items, next]);
    setAddType("");
  };

  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const setDur = (idx, d) => setItems(items.map((it, i) => (i === idx ? { ...it, duration: d } : it)));
  const setFit = (idx, fit) => setItems(items.map((it, i) => (i === idx ? { ...it, fit } : it)));
  const setFontSize = (idx, size) => setItems(items.map((it, i) => (i === idx ? { ...it, font_size: Math.max(12, Math.min(120, Number(size) || 24)) } : it)));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const c = [...items];
    [c[idx], c[j]] = [c[j], c[idx]];
    setItems(c);
  };
  const mediaById = (id) => media.find((m) => m.id === id);
  const getYoutubeId = (url) => {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]">Add Content</div>
        <div className="space-y-4 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-3 md:max-h-80 md:overflow-y-auto">
          <div>
            <div className="text-[9px] font-semibold uppercase text-[#6B7280] mb-2">⚡ Widgets</div>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" className="min-h-11 rounded-lg px-3 text-xs font-semibold justify-start whitespace-normal" onClick={() => addWidget("clock")}>+ Date & Time</Button>
              <Button type="button" variant="outline" className="min-h-11 rounded-lg px-3 text-xs font-semibold justify-start whitespace-normal" onClick={() => addWidget("weather")}>+ Weather</Button>
              <Button type="button" variant="outline" className="min-h-11 rounded-lg px-3 text-xs font-semibold justify-start whitespace-normal" onClick={() => addWidget("bookings")}>+ Bookings</Button>
              <Button type="button" variant="outline" className="min-h-11 rounded-lg px-3 text-xs font-semibold justify-start whitespace-normal" onClick={() => addWidget("queue")}>+ Queue</Button>
              <Button type="button" variant="outline" className="min-h-11 rounded-lg px-3 text-xs font-semibold justify-start whitespace-normal" onClick={() => addWidget("notices")}>+ Notices</Button>
              <Button type="button" variant="outline" className="min-h-11 rounded-lg px-3 text-xs font-semibold justify-start whitespace-normal" onClick={() => addWidget("society_directory")}>+ Society Directory</Button>
              <Button type="button" variant="outline" className="min-h-11 rounded-lg px-3 text-xs font-semibold justify-start whitespace-normal" onClick={() => addWidget("restaurant_menu")}>+ Restaurant Menu</Button>
              {/*<Button type="button" variant="outline" className="h-9 rounded-sm text-xs justify-start" onClick={() => addWidget("products")}>+ Products</Button>
              <Button type="button" variant="outline" className="h-9 rounded-sm text-xs justify-start" onClick={() => addWidget("courses")}>+ Courses</Button>
              <Button type="button" variant="outline" className="h-9 rounded-sm text-xs justify-start col-span-2" onClick={() => addWidget("services")}>+ Services</Button>*/}
            </div>
          </div>

          {/* Media Library Tab */}
          <div>
            <div className="text-[9px] font-semibold uppercase text-[#6B7280] mb-2">📁 Media Library</div>
            {folders.length > 0 && (
              <div className="mb-2 flex gap-1 overflow-x-auto pb-1" aria-label="Media folders">
                {folders.map((folder) => (
                  <button
                    key={folder}
                    type="button"
                    onClick={() => setSelectedFolder(folder)}
                    className={`min-h-9 shrink-0 rounded-lg border px-3 py-1 text-left text-[10px] font-semibold transition ${selectedFolder === folder ? "border-[#111827] bg-[#111827] text-white" : "border-[#D1D5DB] bg-white text-[#374151] hover:border-[#111827]"}`}
                    data-testid={`playlist-media-folder-${folder}`}
                  >
                    {folder} <span className="opacity-70">({media.filter((item) => (item.folder || "default") === folder).length})</span>
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 min-[390px]:grid-cols-3 lg:grid-cols-4">
              {media.length === 0 && <div className="col-span-full rounded-lg border border-dashed border-[#D1D5DB] p-4 text-center text-xs text-[#9CA3AF]">No media uploaded.</div>}
              {media.length > 0 && folderMedia.length === 0 && <div className="col-span-full rounded-lg border border-dashed border-[#D1D5DB] p-4 text-center text-xs text-[#9CA3AF]">No media in this folder.</div>}
              {folderMedia.map((m) => (
                <button key={m.id} type="button" onClick={() => addMedia(m.id)} className="relative aspect-square overflow-hidden rounded-lg border border-[#E5E7EB] bg-white text-[9px] shadow-sm hover:border-[#111827] focus:outline-none focus:ring-2 focus:ring-[#111827]/20" data-testid={`pl-add-${m.id}`}>
                  <MediaThumb item={m} />
                  {isVideoMedia(m) && <div className="absolute left-1 top-1 rounded bg-black/60 p-1 text-white"><Film className="h-3 w-3" /></div>}
                  <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[8px] truncate px-1">{m.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Text Input */}
          <div>
            <div className="text-[9px] font-semibold uppercase text-[#6B7280] mb-2">📝 Text</div>
            <div className="space-y-1">
              <Input value={textValue} onChange={(e) => setTextValue(e.target.value)} placeholder="Enter text..." className="h-11 rounded-lg text-sm" />
              <div className="grid grid-cols-[84px_84px_minmax(0,1fr)] gap-2">
                <Input type="number" min="12" max="120" value={textFontSize} onChange={(e) => setTextFontSize(parseInt(e.target.value || 24, 10))} className="h-11 rounded-lg text-sm" title="Font size" />
                <Input type="number" min="1" max="60" value={newDuration} onChange={(e) => setNewDuration(parseInt(e.target.value || 10))} className="h-11 rounded-lg text-sm" />
                <Button type="button" onClick={addText} className="h-11 rounded-lg bg-blue-600 text-xs font-bold text-white hover:bg-blue-700" disabled={!textValue.trim()}>Add Text</Button>
              </div>
              <p className="text-[10px] text-[#9CA3AF]">Size px · duration sec</p>
            </div>
          </div>

          {/* YouTube URL Input */}
          <div>
            <div className="text-[9px] font-semibold uppercase text-[#6B7280] mb-2">🎥 YouTube</div>
            <div className="space-y-1">
              <Input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." className="h-11 rounded-lg text-sm" />
              <Input value={youtubeTitle} onChange={(e) => setYoutubeTitle(e.target.value)} placeholder="Playing title / song name" className="h-11 rounded-lg text-sm" />
              <div className="flex gap-1">
                <Button type="button" onClick={addYoutube} className="h-11 flex-1 rounded-lg bg-red-600 text-xs font-bold text-white hover:bg-red-700" disabled={!youtubeUrl.trim()}>Add Video</Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]">Sequence ({items.length})</div>
        <div className="space-y-2 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-2 md:max-h-80 md:overflow-y-auto">
          {items.length === 0 && <div className="text-xs text-[#9CA3AF] text-center p-4">Add content on the left.</div>}
          {items.map((it, idx) => {
            const m = it.type === "media" ? mediaById(it.media_id) : null;
            const ytId = it.type === "youtube" ? getYoutubeId(it.url) : null;
            return (
              <div key={idx} className="flex min-w-0 items-stretch gap-2 rounded-xl border border-[#E5E7EB] bg-white p-2 shadow-sm">
                <div className="playlist-sequence-reorder flex w-9 shrink-0 flex-col items-center justify-center gap-1 text-[#6B7280]">
                  <button type="button" onClick={() => move(idx, -1)} className="text-[8px] text-[#6B7280]">▲</button>
                  <button type="button" onClick={() => move(idx, 1)} className="text-[8px] text-[#6B7280]">▼</button>
                </div>
                <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#F3F4F6] sm:h-16 sm:w-16">
                  {it.type === "media" && <MediaThumb item={m} iconClassName="w-3 h-3" />}
                  {it.type === "text" && <div className="text-[8px] font-bold text-[#111827]">TXT</div>}
                  {it.type === "youtube" && ytId && <div className="text-[6px] font-bold text-red-600">YT</div>}
                  {it.type === "clock" && <div className="text-[8px] font-bold text-[#111827]">CLK</div>}
                  {it.type === "weather" && <div className="text-[8px] font-bold text-[#111827]">WTH</div>}
                  {it.type === "bookings" && <div className="text-[8px] font-bold text-[#111827]">BK</div>}
                  {it.type === "queue" && <div className="text-[8px] font-bold text-[#111827]">Q</div>}
                  {it.type === "notices" && <div className="text-[8px] font-bold text-[#111827]">N</div>}
                  {it.type === "society_directory" && <div className="text-[8px] font-bold text-[#111827]">DIR</div>}
                  {it.type === "restaurant_menu" && <div className="text-[8px] font-bold text-[#111827]">MENU</div>}
                  {it.type === "products" && <div className="text-[8px] font-bold text-[#111827]">P</div>}
                  {it.type === "courses" && <div className="text-[8px] font-bold text-[#111827]">C</div>}
                  {it.type === "services" && <div className="text-[8px] font-bold text-[#111827]">S</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold">
                    {it.type === "media" && (m?.name || "Missing media")}
                    {it.type === "text" && `"${it.content.substring(0, 20)}..."`}
                    {it.type === "youtube" && (ytId ? (it.title || "YouTube video") : "Invalid YT URL")}
                    {it.type === "clock" && (it.title || "Date & Time")}
                    {it.type === "weather" && (it.title || "Weather")}
                    {it.type === "bookings" && (it.title || "Today's bookings")}
                    {it.type === "queue" && (it.title || "Queue")}
                    {it.type === "notices" && (it.title || "Notices")}
                    {it.type === "society_directory" && (it.title || "Owner Directory")}
                    {it.type === "restaurant_menu" && (it.title || "Restaurant Menu")}
                    {it.type === "products" && (it.title || "Products")}
                    {it.type === "courses" && (it.title || "Courses")}
                    {it.type === "services" && (it.title || "Services")}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {!(it.type === "youtube" || (it.type === "media" && m?.kind === "video")) ? (
                      <>
                        <Input type="number" min="1" value={it.duration} onChange={(e) => setDur(idx, parseInt(e.target.value || 1))} className="h-10 w-20 rounded-lg text-sm" />
                        <span className="text-[10px] text-[#6B7280]">sec</span>
                      </>
                    ) : (
                      <span className="text-[10px] text-[#6B7280]">Duration managed by media</span>
                    )}
                    {it.type === "text" && (
                      <>
                        <Input type="number" min="12" max="120" value={it.font_size || 24} onChange={(e) => setFontSize(idx, parseInt(e.target.value || 24, 10))} className="h-10 w-20 rounded-lg text-sm" />
                        <span className="text-[10px] text-[#6B7280]">px</span>
                      </>
                    )}
                  </div>
                  {it.type === "media" && (
                    <div className="mt-1">
                      <Select value={it.fit || "cover"} onValueChange={(value) => setFit(idx, value)}>
                        <SelectTrigger className="h-10 w-28 rounded-lg text-xs" data-testid={`media-fit-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cover">Cover</SelectItem>
                          <SelectItem value="contain">Contain</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {it.type === "youtube" && (
                    <Input value={it.title || ""} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, title: e.target.value } : item)))} placeholder="Playing title / song name" className="mt-2 h-10 rounded-lg text-sm" />
                  )}
                  {it.type === "clock" && (
                    <Input value={it.title || ""} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, title: e.target.value } : item)))} placeholder="Header title" className="mt-2 h-10 rounded-lg text-sm" />
                  )}
                  {it.type === "weather" && (
                    <Input value={it.location || ""} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, location: e.target.value } : item)))} placeholder="Weather label" className="mt-2 h-10 rounded-lg text-sm" />
                  )}
                  {it.type === "bookings" && (
                    <Input value={it.title || ""} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, title: e.target.value } : item)))} placeholder="Bookings title" className="mt-2 h-10 rounded-lg text-sm" />
                  )}
                  {it.type === "queue" && (
                    <Input value={it.title || ""} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, title: e.target.value } : item)))} placeholder="Queue title" className="mt-2 h-10 rounded-lg text-sm" />
                  )}
                  {it.type === "notices" && (
                    <Input value={it.title || ""} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, title: e.target.value } : item)))} placeholder="Notices title" className="mt-2 h-10 rounded-lg text-sm" />
                  )}
                  {it.type === "society_directory" && (
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <Input value={it.title || ""} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, title: e.target.value } : item)))} placeholder="Directory title" className="h-10 rounded-lg text-sm" />
                      <Input value={it.wing || "All"} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, wing: e.target.value || "All" } : item)))} placeholder="Wing: All, A, B" className="h-10 rounded-lg text-sm" />
                      <Select value={it.display_mode || "wing_rotation"} onValueChange={(value) => setItems(items.map((item, i) => (i === idx ? { ...item, display_mode: value } : item)))}>
                        <SelectTrigger className="h-10 rounded-lg text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="wing_rotation">Rotate wings</SelectItem>
                          <SelectItem value="selected_wing">Selected wing</SelectItem>
                          <SelectItem value="full_directory">Full directory</SelectItem>
                          <SelectItem value="emergency">Emergency contacts</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="number" min="1" max="40" value={it.rows_per_page || 8} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, rows_per_page: parseInt(e.target.value || 8, 10) } : item)))} placeholder="Rows" className="h-10 rounded-lg text-sm" />
                    </div>
                  )}
                  {it.type === "restaurant_menu" && (
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <Input value={it.title || ""} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, title: e.target.value } : item)))} placeholder="Restaurant name" className="h-10 rounded-lg text-sm" />
                      <Input value={it.tagline || ""} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, tagline: e.target.value } : item)))} placeholder="Optional tagline" className="h-10 rounded-lg text-sm" />
                      <Select value={String(it.columns || 2)} onValueChange={(value) => setItems(items.map((item, i) => (i === idx ? { ...item, columns: Number(value) } : item)))}>
                        <SelectTrigger className="h-10 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 column</SelectItem>
                          <SelectItem value="2">2 columns</SelectItem>
                          <SelectItem value="3">3 columns</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="number" min="12" max="72" value={it.font_size || 24} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, font_size: Math.max(12, Math.min(72, parseInt(e.target.value || 24, 10))) } : item)))} placeholder="Font size" className="h-10 rounded-lg text-sm" title="Menu font size" />
                      <label className="flex min-h-10 items-center gap-2 rounded-lg border border-[#E5E7EB] px-3 text-xs"><input type="checkbox" checked={Boolean(it.show_images)} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, show_images: e.target.checked } : item)))} /> Show item images</label>
                      <label className="flex min-h-10 items-center gap-2 rounded-lg border border-[#E5E7EB] px-3 text-xs"><input type="checkbox" checked={Boolean(it.show_descriptions)} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, show_descriptions: e.target.checked } : item)))} /> Show descriptions</label>
                      <label className="flex min-h-10 items-center gap-2 rounded-lg border border-[#E5E7EB] px-3 text-xs"><input type="checkbox" checked={Boolean(it.auto_rotate)} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, auto_rotate: e.target.checked } : item)))} /> Auto-roll pages</label>
                      <label className="flex min-h-10 items-center gap-2 rounded-lg border border-[#E5E7EB] px-3 text-xs"><input type="checkbox" checked={Boolean(it.manual_navigation)} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, manual_navigation: e.target.checked } : item)))} /> Mouse next/previous</label>
                      <Input type="number" min="3" max="120" value={it.page_seconds || 10} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, page_seconds: Math.max(3, Math.min(120, parseInt(e.target.value || 10, 10))) } : item)))} placeholder="Seconds per page" className="h-10 rounded-lg text-sm" title="Seconds per menu page" />
                      <div className="grid grid-cols-4 gap-1 rounded-lg border border-[#E5E7EB] p-1" title="Background, text, category and price colours">
                        {["background_color", "text_color", "accent_color", "price_color"].map((field) => <input key={field} type="color" value={it[field] || (field === "background_color" ? "#f5f1e8" : field === "accent_color" ? "#233846" : "#17212b")} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, [field]: e.target.value } : item)))} className="h-8 w-full cursor-pointer rounded border-0 bg-transparent p-0" aria-label={field.replaceAll("_", " ")} />)}
                      </div>
                      <p className="text-[10px] text-[#6B7280] sm:col-span-2">Font size also controls row and optional image size. Add only this item for menu-only playback, or combine it with images and videos.</p>
                    </div>
                  )}
                  {["products", "courses", "services"].includes(it.type) && (
                    <>
                      <Input value={it.title || ""} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, title: e.target.value } : item)))} placeholder={`${it.type} title`} className="mt-2 h-10 rounded-lg text-sm" />
                      <Input type="number" min="1" max="20" value={it.limit || 8} onChange={(e) => setItems(items.map((item, i) => (i === idx ? { ...item, limit: parseInt(e.target.value || 8, 10) } : item)))} placeholder="Show count" className="mt-2 h-10 rounded-lg text-sm" />
                    </>
                  )}
                </div>
                <button type="button" onClick={() => remove(idx)} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-xl text-red-600 hover:bg-red-50" aria-label="Delete item">×</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TickerEditor({ items, setItems }) {
  const normalized = Array.isArray(items) ? items : [];
  const addItem = (type) => {
    const next = type === "rss"
      ? { type: "rss", title: "RSS Feed", url: "", duration: 6 }
      : { type: "text", text: "Ticker message", duration: 6 };
    setItems([...normalized, next]);
  };

  const updateItem = (index, patch) => {
    setItems(normalized.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));
  };

  const removeItem = (index) => setItems(normalized.filter((_, itemIndex) => itemIndex !== index));

  return (
    <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Ticker items</div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => addItem("text")} className="rounded-sm">+ Text</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addItem("rss")} className="rounded-sm">+ RSS</Button>
          </div>
        </div>
        <div className="border border-[#E5E7EB] rounded-sm bg-[#F9FAFB] p-2 space-y-2 max-h-[420px] overflow-y-auto">
          {normalized.length === 0 && <div className="text-xs text-[#9CA3AF] text-center p-6">Add scrolling text or RSS feeds for this ticker zone.</div>}
          {normalized.map((item, index) => {
            const type = item?.type || item?.kind || "text";
            return (
              <div key={index} className="bg-white border border-[#E5E7EB] rounded-sm p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Select value={type} onValueChange={(v) => updateItem(index, v === "rss" ? { type: "rss", title: item.title || "RSS Feed", url: item.url || "" } : { type: "text", text: item.text || item.title || "Ticker message" })}>
                    <SelectTrigger className="h-8 w-28 rounded-sm text-xs" data-testid={`ticker-type-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="rss">RSS</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Input type="number" min="1" value={item.duration || 6} onChange={(e) => updateItem(index, { duration: parseInt(e.target.value || 6, 10) })} className="h-8 w-20 rounded-sm text-xs" />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)} className="text-red-600 h-8 px-2">Remove</Button>
                  </div>
                </div>
                {type === "rss" ? (
                  <>
                    <Input value={item.title || ""} onChange={(e) => updateItem(index, { title: e.target.value })} placeholder="Feed label" className="rounded-sm" data-testid={`ticker-title-${index}`} />
                    <Input value={item.url || ""} onChange={(e) => updateItem(index, { url: e.target.value })} placeholder="https://example.com/feed.xml" className="rounded-sm font-mono" data-testid={`ticker-url-${index}`} />
                  </>
                ) : (
                  <Input value={item.text || item.title || ""} onChange={(e) => updateItem(index, { text: e.target.value, title: e.target.value })} placeholder="Scrolling message" className="rounded-sm" data-testid={`ticker-text-${index}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="rounded-sm border border-[#E5E7EB] bg-black text-white p-4 overflow-hidden">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-white/50 mb-3">Ticker preview</div>
        <div className="h-20 flex items-center overflow-hidden border border-white/10 rounded-sm bg-black/80">
          <div className="whitespace-nowrap animate-marquee inline-flex gap-8 px-4 text-sm font-semibold text-white">
            {(normalized.length ? normalized : [{ type: "text", text: "Ticker message" }]).map((item, index) => (
              <span key={index} className="inline-flex items-center gap-2">
                <span className="text-white/40 uppercase text-[10px]">{(item.type || item.kind || "text").toUpperCase()}</span>
                <span>{item.text || item.title || item.url || "Message"}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientPlaylists() {
  const [playlists, setPlaylists] = useState([]);
  const [media, setMedia] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeZone, setActiveZone] = useState("");
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      const [p, m, t, b] = await Promise.all([api.get("/client/playlists"), api.get("/client/media"), api.get("/client/templates"), api.get("/client/branches")]);
      setPlaylists(p.data);
      setMedia(m.data);
      setTemplates(t.data);
      setBranches(Array.isArray(b.data) ? b.data : []);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const activeTemplate = useMemo(() => templates.find((t) => t.id === form.template_id) || null, [templates, form.template_id]);
  const zoneDefs = useMemo(() => {
     if (!activeTemplate) return FALLBACK_TEMPLATE.layout.zones;
     const extracted = extractZonesFromTemplate(activeTemplate);
     return extracted.length > 0 ? extracted : FALLBACK_TEMPLATE.layout.zones;
  }, [activeTemplate]);

  useEffect(() => {
    if (!zoneDefs.length) return;
    if (!zoneDefs.some((zone) => zone.id === activeZone)) {
      setActiveZone(zoneDefs[0].id);
    }
  }, [zoneDefs, activeZone]);

  const applyTemplate = (templateId, existingZoneItems = {}) => {
    const tpl = templates.find((t) => t.id === templateId);
    const zones = tpl ? extractZonesFromTemplate(tpl) : FALLBACK_TEMPLATE.layout.zones;
    const { ticker_messages: existingTickerMessages, ...zoneSource } = existingZoneItems || {};
    const nextZoneItems = remapLegacyItemsToZones(zones, zoneSource);
    zones.forEach((zone) => {
      if (isAutoZone(zone)) nextZoneItems[zone.id] = [];
    });
    const nextTickerMessages = Array.isArray(existingTickerMessages) ? existingTickerMessages : Array.isArray(zoneSource.ticker) ? zoneSource.ticker : [];
    delete nextZoneItems.ticker;
    setForm((prev) => ({ ...prev, template_id: templateId, zone_items: nextZoneItems, ticker_messages: nextTickerMessages }));
  };

  const openCreate = () => {
    setEditing(null);
    const defaultTemplate = templates[0]?.id || "";
    setForm(defaultTemplate ? { ...emptyForm, template_id: defaultTemplate } : emptyForm);
    setOpen(true);
    if (defaultTemplate) applyTemplate(defaultTemplate, {});
    setActiveZone(zoneDefs[0]?.id || "main");
  };

  const openEdit = (playlist) => {
    setEditing(playlist);
    const templateId = playlist.template_id || templates[0]?.id || "";
    const mappedZoneItems = normalizeZoneItems(playlist.zone_items);
    const tickerMessages = Array.isArray(playlist.ticker_messages) ? playlist.ticker_messages : mappedZoneItems.ticker || [];
    delete mappedZoneItems.ticker;
    if (!Object.keys(mappedZoneItems).length) {
      if (playlist.main_items) mappedZoneItems.main = playlist.main_items;
      if (playlist.sidebar_items) mappedZoneItems.sidebar = playlist.sidebar_items;
    }
    setForm({ name: playlist.name, branch_id: playlist.branch_id || "", template_id: templateId, zone_items: mappedZoneItems, ticker_messages: tickerMessages });

     setTimeout(() => {
    console.log("📂 AFTER SETFORM - form.ticker_messages:", form.ticker_messages);
  }, 100);
  
    setOpen(true);
    if (templateId) applyTemplate(templateId, { ...mappedZoneItems, ticker_messages: tickerMessages });
    setActiveZone(zoneDefs[0]?.id || "main");
  };

  const submit = async (e) => {
    e.preventDefault();
     console.log("🔵 SUBMIT - form.ticker_messages BEFORE:", form.ticker_messages);
    console.log("🔵 SUBMIT - form.zone_items:", form.zone_items);
    try {
      const payload = {
        name: form.name,
        branch_id: form.branch_id || null,
        template_id: form.template_id || null,
        zone_items: form.zone_items,
        ticker_messages: form.ticker_messages || [],
      };
        console.log("🔵 SUBMIT - payload.ticker_messages:", payload.ticker_messages);
      if (editing) await api.put(`/client/playlists/${editing.id}`, payload);
      else await api.post("/client/playlists", payload);
      toast.success(editing ? "Playlist updated" : "Playlist created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this playlist?")) return;
    try {
      await api.delete(`/client/playlists/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const folders = Array.from(new Set(media.map((m) => m.folder || "default")));

  return (
    <div data-testid="client-playlists-page">
      <PageHeader overline="Client / Playlists" title="Playlists." subtitle="Select a template, then choose media for each zone in that screen layout.">
        <Button onClick={openCreate} className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white" data-testid="create-playlist-btn">
          <Plus className="w-4 h-4 mr-2" /> New Playlist
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {playlists.length === 0 && (
          <div className="col-span-full text-center py-12 text-sm text-[#6B7280] border border-dashed border-[#E5E7EB] rounded-sm">No playlists yet.</div>
        )}
        {playlists.map((p) => {
          const zoneItems = p.zone_items || {};
          const total = Object.values(zoneItems).reduce((sum, list) => sum + (list?.length || 0), 0) + (p.ticker_messages?.length || 0);
          return (
            <div key={p.id} className="dense-card bg-white border border-[#E5E7EB] rounded-sm p-5" data-testid={`playlist-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-display text-xl font-extrabold tracking-tight">{p.name}</h3>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-playlist-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(p.id)} className="text-red-600" data-testid={`delete-playlist-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div className="text-xs text-[#6B7280] mb-3">Branch: <span className="font-semibold text-[#111827]">{p.branch_name || branches.find((b) => b.id === p.branch_id)?.name || "Global / no branch"}</span></div>
              <div className="text-xs text-[#6B7280] mb-3">Template: <span className="font-semibold text-[#111827]">{templates.find((t) => t.id === p.template_id)?.name || "Default"}</span></div>
              <div className="text-xs text-[#6B7280] mb-3">{total} item(s) across {Object.keys(zoneItems).length || 0} zone(s){(p.ticker_messages?.length || 0) ? ` + ${p.ticker_messages.length} ticker item(s)` : ""}</div>
              <div className="space-y-2">
                {Object.entries(zoneItems).map(([zoneId, list]) => (
                  <div key={zoneId} className="bg-[#F9FAFB] rounded-sm p-2 border border-[#E5E7EB]">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">{zoneId}</div>
                    <div className="text-xs text-[#111827]">{(list || []).length} item(s)</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="grid max-h-[calc(100svh-1rem)] w-[calc(100vw-1rem)] max-w-5xl grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-2xl p-0 sm:rounded-sm" data-testid="playlist-dialog">
          <DialogHeader className="border-b border-[#E5E7EB] px-4 py-4 text-center sm:px-6">
            <DialogTitle className="font-display text-3xl font-extrabold tracking-tight sm:text-2xl">{editing ? "Edit Playlist" : "New Playlist"}</DialogTitle>
            <DialogDescription>Select template and add media, text, or YouTube videos to zones</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="flex min-h-0 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 pb-24 sm:px-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11 rounded-lg md:h-10 md:rounded-sm" data-testid="pl-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Branch</Label>
                <Select value={form.branch_id || "none"} onValueChange={(value) => setForm({ ...form, branch_id: value === "none" ? "" : value })}>
                  <SelectTrigger className="h-11 rounded-lg md:h-10 md:rounded-sm" data-testid="playlist-branch-select">
                    <SelectValue placeholder="Global / no branch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Global / no branch</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name || branch.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Template</Label>
                <Select value={form.template_id || ""} onValueChange={(value) => applyTemplate(value, { ...form.zone_items, ticker_messages: form.ticker_messages })}>
                  <SelectTrigger className="h-11 rounded-lg md:h-10 md:rounded-sm" data-testid="playlist-template-select">
                    <SelectValue placeholder="Choose template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-xl border border-[#E5E7EB] bg-white p-3 md:rounded-sm">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280] mb-2">Template Preview</div>
              <div className="grid grid-cols-2 gap-2 min-[390px]:grid-cols-3">
                {zoneDefs.map((zone) => (
                  <div key={zone.id} className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-center text-xs font-bold text-[#6B7280] md:rounded-sm md:border-[#111827]">
                    {zone.name}
                  </div>
                ))}
              </div>
            </div>

            <Tabs value={activeZone} onValueChange={setActiveZone} className="space-y-4">
              <TabsList className="flex h-auto max-w-full justify-start gap-2 overflow-x-auto rounded-xl bg-[#F3F4F6] p-2 md:flex-wrap md:rounded-sm">
                {zoneDefs.map((zone) => (
                  <TabsTrigger key={zone.id} value={zone.id} className="min-h-11 shrink-0 rounded-lg px-4 data-[state=active]:bg-[#111827] data-[state=active]:text-white md:min-h-9 md:rounded-sm" data-testid={`zone-tab-${zone.id}`}>
                    {zone.name}
                      <span className="ml-2 text-[10px] font-mono opacity-70">{isTickerZone(zone) ? (form.ticker_messages || []).length : isAutoZone(zone) ? "auto" : (form.zone_items?.[zone.id] || []).length}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
              {zoneDefs.map((zone) => (
                <TabsContent key={zone.id} value={zone.id}>
                  {isTickerZone(zone) ? (
                    <TickerEditor
  items={form.ticker_messages || []}
  setItems={(nextItems) => {
    console.log("🟣 TickerEditor setItems called with:", nextItems);
    setForm({ ...form, ticker_messages: nextItems });
  }}
/>
                  ) : isAutoZone(zone) ? (
                    <div className="rounded-sm border border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-4 text-sm text-[#6B7280]">
                      <div className="font-semibold text-[#111827]">Automatic queue zone</div>
                      <p className="mt-1">
                        This zone is filled by the player using the live queue, showing the current token and the next tokens automatically.
                        No manual media items are needed here.
                      </p>
                    </div>
                  ) : (
                    <ZonePicker
                      media={media}
                      items={form.zone_items?.[zone.id] || []}
                      setItems={(nextItems) => setForm({ ...form, zone_items: { ...form.zone_items, [zone.id]: nextItems } })}
                    />
                  )}
                </TabsContent>
              ))}
            </Tabs>

            <div className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-xs text-[#6B7280] md:rounded-sm">
              Media folders available: {folders.length ? folders.join(", ") : "default"}
            </div>
            </div>

            <DialogFooter className="sticky bottom-0 z-10 border-t border-[#E5E7EB] bg-white/95 px-4 py-3 backdrop-blur sm:px-6">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="h-11 flex-1 rounded-lg sm:flex-none sm:rounded-sm">Cancel</Button>
              <Button type="submit" className="h-11 flex-1 rounded-lg bg-[#111827] text-white hover:bg-[#374151] sm:flex-none sm:rounded-sm" data-testid="pl-submit">{editing ? "Save" : "Create Playlist"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
